import * as Sentry from '@sentry/react';
import { env } from '@/env';

export const initializeSentry = () => {
  if (!env.VITE_SENTRY_DSN) {
    console.warn(
      'VITE_SENTRY_DSN not provided - Sentry will not be initialized'
    );
    return;
  }

  try {
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      environment: env.MODE,

      // Release tracking with Vercel git commit SHA
      release: import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA || 'unknown',

      // Performance monitoring
      tracesSampleRate: env.MODE === 'production' ? 0.1 : 1.0,

      // Error sampling - more conservative in production
      sampleRate: env.MODE === 'production' ? 0.5 : 1.0,

      // Session Replay configuration
      replaysSessionSampleRate: env.MODE === 'production' ? 0.1 : 1.0,
      replaysOnErrorSampleRate: env.MODE === 'production' ? 0.5 : 1.0,

      // Enable debug mode in development when needed
      // debug: env.MODE === 'development',

      // Configure beforeSend to filter out certain errors
      beforeSend(event, hint) {
        // Don't send errors in development unless explicitly configured
        if (env.MODE === 'development' && !env.VITE_SENTRY_DEBUG) {
          return null;
        }

        // Filter out certain types of errors
        const error = hint?.originalException;
        if (error instanceof Error) {
          // Check error name/type for more reliable filtering
          if (error.name === 'NetworkError' || error.name === 'FetchError') {
            return null;
          }

          // For HTTP errors, check status code if available
          const httpError = error as Error & { status?: number };
          if (httpError.status === 401 || httpError.status === 403) {
            return null;
          }

          // Don't send validation errors (these are expected)
          if (
            error.message.includes('Validation failed') ||
            error.message.includes('Invalid input')
          ) {
            return null;
          }

          // Don't send authentication errors (these are expected)
          if (
            error.message.includes('Unauthorized') ||
            error.message.includes('Forbidden')
          ) {
            return null;
          }
        }

        return event;
      },

      // Configure integrations
      integrations: [
        // Browser integrations
        Sentry.browserTracingIntegration(),
        // Session Replay (enabled with sampling rates above)
        Sentry.replayIntegration({
          maskAllText: env.MODE === 'production' ? true : false,
          blockAllMedia: env.MODE === 'production' ? true : false,
        }),
      ],

      // Set maximum event size
      maxBreadcrumbs: 100,
    });
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

// Export Sentry for use in other parts of the application
export { Sentry };

// Helper function to capture errors with context
export const captureError = (
  error: Error,
  context?: Record<string, unknown>
) => {
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};

// Helper function to capture messages
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

// Helper function to add breadcrumbs
export const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb(breadcrumb);
};

// Helper function to add API interaction breadcrumbs
export const addApiBreadcrumb = (
  method: string,
  endpoint: string,
  status?: number
) => {
  addBreadcrumb({
    message: `API ${method}: ${endpoint}`,
    category: 'api',
    level: 'info',
    data: {
      method,
      endpoint,
      status,
    },
  });
};

// Helper function to capture API errors with context
export const captureApiError = (
  error: Error,
  method: string,
  endpoint: string,
  context?: Record<string, unknown>
) => {
  captureError(error, {
    operation: 'api_call',
    method,
    endpoint,
    ...context,
  });
};
